import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { ListPageShell, StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = '/api/Tenants/Usage';

const FLAG_LABEL = {
  LICENSE_EXPIRED: { text: 'Lisans Süresi Doldu', tone: 'bad' },
  LICENSE_EXPIRING_SOON: { text: 'Lisans Bitişi Yakın', tone: 'wait' },
  OVER_USER_LIMIT: { text: 'Koltuk Limiti Aşıldı', tone: 'bad' },
  NEAR_USER_LIMIT: { text: 'Koltuk Limitine Yaklaştı', tone: 'wait' },
  NO_ACTIVITY_30D: { text: '30 Gündür Aktivite Yok', tone: 'wait' },
};

// Banner'da SuperAdmin'in karar vermesi gereken lisans/koltuk uyarıları (aktivite hariç).
const LICENSE_ATTENTION_FLAGS = new Set([
  'LICENSE_EXPIRED', 'LICENSE_EXPIRING_SOON', 'OVER_USER_LIMIT', 'NEAR_USER_LIMIT',
]);

const chip = {
  display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11,
  background: 'var(--surface-2, rgba(15,23,42,0.06))', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
};

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.10))', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' };
const numTd = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function fmtDate(v) {
  if (!v) return '-';
  try { return new Date(v).toLocaleDateString('tr-TR'); } catch { return '-'; }
}

export function TenantUsagePage() {
  usePageMeta('Tenant Kullanım & Lisans', 'Tenant başına kullanım, lisans ve aktivite özeti');
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, { headers: authHeaders() });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.success === false) throw new Error(data?.message || 'Kullanım verisi alınamadı');
      const list = data?.data ?? data?.value ?? data ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      showToast('error', e.message || 'Yükleme hatası');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.container}>
      <ListPageShell
        title="Tenant Kullanım & Lisans"
        subtitle="Tenant başına kullanıcı/müşteri/ürün/sipariş sayıları, lisans ve son aktivite."
        actions={
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button style={s.btnSecondary} onClick={() => navigate('/tenants')}>Tenant'lar</button>
            <button style={s.btnSecondary} onClick={load}>Yenile</button>
          </span>
        }
      >
        {(() => {
          const attention = rows.filter((r) => (r.healthFlags || []).some((f) => LICENSE_ATTENTION_FLAGS.has(f)));
          if (loading || attention.length === 0) return null;
          return (
            <div style={{
              marginBottom: 14, padding: '12px 14px', borderRadius: 10,
              background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.30)',
            }}>
              <div style={{ fontWeight: 700, color: '#B45309', marginBottom: 6 }}>
                ⚠ Dikkat gerektiren {attention.length} tenant
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {attention.map((r) => (
                  <span key={r.tenantId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...chip, padding: '3px 10px' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{r.name || r.identifier}</strong>
                    {(r.healthFlags || []).filter((f) => LICENSE_ATTENTION_FLAGS.has(f)).map((f) => {
                      const meta = FLAG_LABEL[f] || { text: f, tone: 'neutral' };
                      return <StatusBadge key={f} tone={meta.tone}>{meta.text}</StatusBadge>;
                    })}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Tenant</th>
                <th style={{ ...th, textAlign: 'right' }}>Koltuk (Mobil)</th>
                <th style={{ ...th, textAlign: 'right' }}>Müşteri</th>
                <th style={{ ...th, textAlign: 'right' }}>Ürün</th>
                <th style={{ ...th, textAlign: 'right' }}>Sipariş</th>
                <th style={th}>Son Sipariş</th>
                <th style={th}>Lisans</th>
                <th style={th}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={td} colSpan={8}>Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td style={td} colSpan={8}>Kayıt bulunamadı</td></tr>
              ) : rows.map((r) => {
                const lic = r.license;
                const max = lic?.maxUsers || 0;
                const seat = r.mobileUserCount ?? 0;
                const overUser = max > 0 && seat > max;
                const nearUser = max > 0 && !overUser && seat >= Math.ceil(max * 0.9);
                const seatColor = overUser ? '#E53E3E' : nearUser ? '#D97706' : 'inherit';
                const features = Array.isArray(lic?.features) ? lic.features : [];
                return (
                  <tr key={r.tenantId}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name || '-'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.identifier}</div>
                    </td>
                    <td style={numTd}>
                      <span style={{ color: seatColor, fontWeight: overUser || nearUser ? 700 : 400 }}>
                        {seat}{max ? ` / ${max}` : ''}
                      </span>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }} title="Toplam kullanıcı (web + mobil)">
                        Toplam: {r.userCount}
                      </div>
                    </td>
                    <td style={numTd}>{r.customerCount}</td>
                    <td style={numTd}>{r.productCount}</td>
                    <td style={numTd}>{r.orderCount}</td>
                    <td style={td}>{fmtDate(r.lastOrderDate)}</td>
                    <td style={td}>
                      {lic ? (
                        <div>
                          <div>{lic.edition || '-'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {lic.expiresAt ? `${fmtDate(lic.expiresAt)} (${lic.daysRemaining ?? '?'} gün)` : 'Süresiz'}
                          </div>
                          {features.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, maxWidth: 260 }}
                              title={`Aktif feature'lar: ${features.join(', ')}`}>
                              {features.slice(0, 4).map((f) => <span key={f} style={chip}>{f}</span>)}
                              {features.length > 4 && <span style={chip}>+{features.length - 4}</span>}
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--text-secondary)' }}>Tanımsız</span>}
                    </td>
                    <td style={td}>
                      {(r.healthFlags && r.healthFlags.length > 0) ? (
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                          {r.healthFlags.map((f) => {
                            const meta = FLAG_LABEL[f] || { text: f, tone: 'neutral' };
                            return <StatusBadge key={f} tone={meta.tone}>{meta.text}</StatusBadge>;
                          })}
                        </span>
                      ) : <StatusBadge tone="ok">Sağlıklı</StatusBadge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}
